import { useId } from 'react';
import { Link } from 'react-router-dom';
import { Menu, MenuItem, PopoverNext } from '@blueprintjs/core';
import { HintButton as Button } from '@/components/base/HintButton';
import { discoveryLayout } from '../../styled-system/recipes';
import { hasRowTextSelection, useRowPointerGesture } from '@/discovery/rowActivation';
import { projectListStyles } from './ProjectList.styles';
import { ProjectLastOpened } from './ProjectLastOpened';
const styles = discoveryLayout();

function ProjectOpenLink({ name, openedAt }: { name: string; openedAt?: number }) {
  const gesture = useRowPointerGesture();
  const openedId = useId();
  return (
    <Link
      to={`/editor?project=${encodeURIComponent(name)}`}
      className={`${styles.projectOpen} ${projectListStyles.link}`}
      aria-label={`Open ${name}`}
      aria-describedby={openedId}
      draggable={false}
      {...gesture.handlers}
      onClick={(event) => {
        if (
          event.detail > 0 &&
          (gesture.wasDragged() || hasRowTextSelection(event.currentTarget))
        )
          event.preventDefault();
      }}
    >
      <span className={styles.rowTitle}>{name}</span>
      <ProjectLastOpened id={openedId} timestamp={openedAt} />
    </Link>
  );
}

export function ProjectListHeader({
  status = 'ready',
  actionFailed = false,
}: {
  status?: 'loading' | 'ready' | 'error';
  actionFailed?: boolean;
}) {
  const announcement =
    status === 'loading'
      ? 'Loading saved projects…'
      : status === 'error'
        ? 'Unable to refresh projects. Details below.'
        : actionFailed
          ? 'Project action failed. Details below.'
          : undefined;
  return (
    <div className={projectListStyles.header}>
      <span aria-label="Name">
        <span
          role="status"
          aria-live="polite"
          aria-atomic="true"
          aria-label={announcement}
        >
          {status === 'loading'
            ? 'Loading…'
            : status === 'error'
              ? 'Load failed'
              : actionFailed
                ? 'Action failed'
                : 'Name'}
        </span>
      </span>
      <span>Last opened</span>
      <span>Actions</span>
    </div>
  );
}

export function ProjectListRows({
  names,
  lastOpened,
  onRename,
  onExport,
  onShare,
  onDuplicate,
  onDelete,
}: {
  names: readonly string[];
  lastOpened: ReadonlyMap<string, number>;
  onRename: (name: string) => void;
  onExport: (name: string) => void;
  onShare?: (name: string) => void;
  onDuplicate: (name: string) => void;
  onDelete: (name: string) => void;
}) {
  return (
    <>
      {names.map((name) => (
        <div key={name} className={styles.projectRow}>
          <ProjectOpenLink name={name} openedAt={lastOpened.get(name)} />
          <div className={styles.projectActions}>
            <PopoverNext
              placement="bottom-end"
              content={
                <Menu aria-label={`Actions for ${name}`}>
                  <MenuItem icon="edit" text="Rename" onClick={() => onRename(name)} />
                  <MenuItem icon="export" text="Export" onClick={() => onExport(name)} />
                  {onShare && (
                    <MenuItem
                      icon="share"
                      text="Share project"
                      onClick={() => onShare(name)}
                    />
                  )}
                  <MenuItem
                    icon="duplicate"
                    text="Duplicate"
                    onClick={() => onDuplicate(name)}
                  />
                  <MenuItem
                    icon="trash"
                    intent="danger"
                    text="Delete"
                    onClick={() => onDelete(name)}
                  />
                </Menu>
              }
            >
              <Button
                icon="more"
                variant="minimal"
                aria-label={`Actions for ${name}`}
                title={`Actions for ${name}`}
              />
            </PopoverNext>
          </div>
        </div>
      ))}
    </>
  );
}
